import React, { useEffect, useState } from 'react';
import { attachCDItemsToRemittanceItems, toRemoveAttached } from './attachCDItemsToRemittanceItems';
import { RemittanceList } from './RemittanceList';
import { useAppDispatch } from '../../../../store/store';
import { useSelector } from 'react-redux';
import { RootStore } from '../../../../store/types';
import { useKeyToSelect } from '../../../../store/createKeyedDataAsyncSlice';
import { ErrorMessage } from '../../../../ui-toolkit';
import {
  getRemittanceAction,
  getRemittanceSlice,
  sendRemittanceAction,
  sendRemittanceSlice,
} from '../../../../store/order/escrow/remittenenceSlice';
import { ISendUnderwriterRemittanceRequest } from '../../../../API/order/escrow/EscrowAPI';
import { loadEscrowBalancesAsync } from '../../../../store/order/escrow/escrowBalancesHeaderSlice';
import { CheckForAvailableFundsModal } from '../escrowCreateEdit/checkForAvailableFundsModal/CheckForAvailableFundsModal';
import { initCdItems, loadItemsData } from '../../../../store/order/cdBuilder';
import { useSelectCurrentOrder } from '../../../../selectors/order/selectCurrentOrder';
import { EscrowBaseAPI, useGetDetailedEscrowDataQuery } from 'API/order/escrow/EscrowBaseAPI';
import { eAPITags } from 'API/eAPITags';
import { eTransactionCategory } from '../../../../ui-toolkit/enums/eTransactionType';

export const EscrowRemittanceACHContainer: React.FC<{
  orderId: string;
  escrowFileId: string | undefined;
  americanDigitalAchId: string;
}> = ({ orderId, escrowFileId, americanDigitalAchId }) => {
  const dispatch = useAppDispatch();
  const { data: order } = useSelectCurrentOrder();

  useEffect(() => {
    dispatch(initCdItems(orderId, []));
    dispatch(getRemittanceAction(orderId));
    dispatch(
      loadItemsData(orderId, [], {
        ownerType: 'order',
        transactionType: order.transaction.transaction_category || eTransactionCategory.Refinance,
        loanAmount: 0,
        addressInfo: { state: '', street_address: '', city: '', zip_code: '' },
      })
    );
    dispatch(EscrowBaseAPI.util.invalidateTags([{ type: eAPITags.DetailedEscrowData, id: orderId }]));
  }, [dispatch, order.transaction.transaction_category, orderId]);

  const { escrowBalancesHeaderSlice } = useSelector((store: RootStore) => store.order.escrow);

  const { fetchError: sendRemittanceFetchError, fetchStatus: sendRemittanceFetchStatus } = useKeyToSelect(
    escrowFileId!,
    (store: RootStore) => store.order.escrow.sendRemittanceSlice
  );

  useEffect(() => {
    if (sendRemittanceFetchStatus.success) {
      setTimeout(() => {
        dispatch(sendRemittanceSlice.actions.reset({}));
        dispatch(getRemittanceSlice.actions.reset({}));
        dispatch(getRemittanceAction(orderId));
        dispatch(loadEscrowBalancesAsync({ orderId }));
      }, 3000);
    }
  }, [sendRemittanceFetchStatus, dispatch, orderId]);

  // remittance + cd items data
  const cdItemsData = useSelector((rootStore: RootStore) => rootStore.order.cdBuilder.cdItems);
  const {
    data: remittance,
    fetchError: getRemittanceFetchError,
    fetchStatus: getRemittanceFetchStatus,
  } = useKeyToSelect(orderId, (store: RootStore) => store.order.escrow.remittanceSlice);

  const { data: escrowData } = useGetDetailedEscrowDataQuery(orderId);
  // escrow complete data

  const escrowBankAccount = escrowData?.depositEscrowBankAccount;

  const remittanceCDItemPairs =
    remittance &&
    attachCDItemsToRemittanceItems(remittance.filter(toRemoveAttached), cdItemsData[orderId]?.items.asArray || []);
  const remittanceError =
    remittanceCDItemPairs === null ? new Error(`Have an ACH Remittance to show that doesn't have a Fees Item`) : null;

  const pageError = getRemittanceFetchError || sendRemittanceFetchError || remittanceError;
  const [showWarningModal, setShowWarningModal] = useState(false);

  const onSend = async () => {
    if (remittanceCDItemPairs) {
      const remittanceList = remittanceCDItemPairs.map((t) => t.remittance);
      // do the rails thing
      const availableAmount = escrowBalancesHeaderSlice.data.availableAmountDollars * 100;
      const totalAmount = remittanceList.reduce((p, c) => p + c.underwriterFeeAmountCents, 0);
      if (totalAmount > availableAmount) {
        setShowWarningModal(true);
      } else {
        // trigger send
        await sendRemittances();
      }
    }
  };

  const sendRemittances = async () => {
    if (remittanceCDItemPairs && escrowBankAccount && escrowFileId) {
      const remittanceList = remittanceCDItemPairs.map((t) => t.remittance);
      const sendData: ISendUnderwriterRemittanceRequest = {
        escrowFileId: escrowFileId,
        remittanceData: {
          remittanceIds: remittanceList.map((t) => t.id),
          escrowBankAccountId: escrowBankAccount.id,
          achAccountId: americanDigitalAchId,
        },
      };
      await dispatch(sendRemittanceAction(sendData));
      setShowWarningModal(false);
    }
  };

  return (
    <>
      <ErrorMessage error={pageError} />
      {remittanceCDItemPairs && remittanceCDItemPairs.length ? (
        <RemittanceList
          remittanceCDItemPairs={remittanceCDItemPairs}
          onSend={onSend}
          fetchStatus={getRemittanceFetchStatus}
        />
      ) : null}
      {showWarningModal && (
        <CheckForAvailableFundsModal
          submitData={() => sendRemittances()}
          type="ach"
          handleClose={() => setShowWarningModal(false)}
        />
      )}
    </>
  );
};
